/**
 * Content fingerprinting for upload de-duplication.
 *
 * S3 ETags for single-part PUTs are the hex MD5 of the object bytes, so we
 * compute the same client-side and compare. We hash in chunks to handle large
 * files without exhausting memory.
 */
import SparkMD5 from 'spark-md5';

const CHUNK = 4 * 1024 * 1024; // 4 MB

/** Read a Blob slice as an ArrayBuffer, working in both browsers and jsdom. */
function readArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsArrayBuffer(blob);
  });
}

/** Hex MD5 of a File/Blob — matches the S3 ETag for single-part uploads. */
export async function md5Hex(file: Blob): Promise<string> {
  const spark = new SparkMD5.ArrayBuffer();
  for (let offset = 0; offset < file.size; offset += CHUNK) {
    const slice = file.slice(offset, offset + CHUNK);
    spark.append(await readArrayBuffer(slice));
  }
  return spark.end();
}

/**
 * Given the existing files in a target event and a candidate File, decide
 * whether the candidate's content already exists. Uses size as a cheap
 * pre-filter, then MD5 only when a size matches (avoids hashing every file).
 */
export async function isDuplicateContent(
  file: File,
  existingBySize: Map<number, string[]>,
): Promise<boolean> {
  const sameSizeEtags = existingBySize.get(file.size);
  if (!sameSizeEtags || sameSizeEtags.length === 0) return false;
  const hash = await md5Hex(file);
  return sameSizeEtags.includes(hash);
}

/** Index existing originals by size -> [etag,...] for quick candidate lookup. */
export function indexBySize(
  files: Array<{ size?: number; etag?: string }>,
): Map<number, string[]> {
  const map = new Map<number, string[]>();
  for (const f of files) {
    if (typeof f.size !== 'number' || !f.etag) continue;
    const list = map.get(f.size);
    if (list) list.push(f.etag);
    else map.set(f.size, [f.etag]);
  }
  return map;
}
